/*
 *  -----------------------------------------------------------------------
 *    <copyright file="unassign-crf-confirmation-dialog.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import { useMutation } from '@apollo/client/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DialogContent } from '@mui/material';
import React from 'react';
import { useTranslation } from 'hooks';
// eslint-disable-next-line max-len
import { StudyVisitScheduleFields_StudySubjectVisitScheduleTemplate_visits_StudySubjectVisit as StudyVisitScheduleFields_StudySubjectVisit } from 'queries/generated/fragments.generated';
import { SAVE_STUDY_VISIT_SCHEDULE_VISIT } from 'queries/study-visit-schedules';
import Button from 'views/components/core/buttons';
import { CustomDialog, DialogActions, DialogTitle } from 'views/components/core/dialog';

interface UnassignCRFConfirmationDialogInterface {
    studyId: string;
    currentScheduleId: string | null | undefined;
    currentCRF: any;
    currentVisit: StudyVisitScheduleFields_StudySubjectVisit;
    visitCRFs: string[];
    open: boolean;
    close: (action?: string) => void;
}

const UnassignCRFConfirmationDialog = ({
    studyId,
    currentScheduleId,
    currentCRF,
    currentVisit,
    visitCRFs,
    open,
    close,
}: UnassignCRFConfirmationDialogInterface) => {
    const { t } = useTranslation();
    const [saveVisit] = useMutation(SAVE_STUDY_VISIT_SCHEDULE_VISIT);

    const onClose = (action?: string) => {
        close(action);
    };

    const unassignSubmit = () => {
        const remainingIds = visitCRFs.filter((crfId) => crfId !== currentCRF.id);

        const submitData = {
            studyId,
            visitScheduleId: currentScheduleId,
            visit: {
                id: currentVisit.id,
                active: currentVisit.active,
                sequenceNumber: currentVisit.sequenceNumber,
                type: currentVisit.type,
                visitId: currentVisit.visitId,
                visitName: currentVisit.visitName,
                leadVisitId: currentVisit.leadVisitId,
                leadPeriod: currentVisit.leadPeriod,
                leadUnit: currentVisit.leadUnit,
                minWinPeriod: currentVisit.minWinPeriod,
                maxWinPeriod: currentVisit.maxWinPeriod,
                minWinUnit: currentVisit.minWinUnit,
                minOccurrences: currentVisit.minOccurrences,
                forecastEndDateField: currentVisit.forecastEndDateField,
                isEndDateEarliest: currentVisit.isEndDateEarliest,
                eventIds: remainingIds,
            },
        };

        saveVisit({
            variables: {
                ...submitData,
            },
        })
            .then(() => {
                onClose('unassign');
            })
            .catch(() => {
                onClose('error');
            });
    };

    return (
        <CustomDialog
            onClose={() => {
                onClose();
            }}
            fullWidth
            maxWidth="md"
            scroll="paper"
            open={open}
            className="custom-width-popup"
        >
            <DialogTitle id="unassign-crf-visit-modal-title">
                {t('admin:unassignCRF')}
                <FontAwesomeIcon
                    icon={['fal', 'times']}
                    className="fa-lg close-icon-cv"
                    onClick={() => {
                        onClose();
                    }}
                    data-testid="close-dialog"
                />
            </DialogTitle>
            <DialogContent className="unassign-crf-dialog-content">
                <div className="delete-visit-text">{t('admin:unassignVisitCrfQuestion')}</div>
                {currentVisit && (
                    <div className="content-text">
                        <span className="title">{t('subject:visitName')}:</span>
                        {currentVisit.visitName}
                    </div>
                )}
                {currentCRF && (
                    <div className="content-text">
                        <span className="title">{t('admin:crfName')}:</span>
                        {currentCRF.crfName}
                    </div>
                )}
                <DialogActions className="add-treatment-dialog-content-actions flex-end">
                    <Button id="confirm-unassign-crf-btn" text={t('common:unassign')} onClick={unassignSubmit} />
                </DialogActions>
            </DialogContent>
        </CustomDialog>
    );
};

export default UnassignCRFConfirmationDialog;
